import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RadioChips, Sheet, SheetContent } from '@tg-web/components';

import { useMainButton } from '../../../shared/lib/useMainButton';
import { getNotificationType } from '../lib';
import { NotificationType } from '../model';
import { CustomNotificationInput } from './CustomNotificationInput';

export type InviteInstructionModalProps = {
  initialValue: string | null;
  isOpen: boolean;
  onClose: VoidFunction;
  onSubmit: (value: string) => void;
};

const maxValues: Record<NotificationType, number> = {
  [NotificationType.days]: 30,
  [NotificationType.hours]: 24,
  [NotificationType.minutes]: 60,
};
const multipliers: Record<NotificationType, number> = {
  [NotificationType.days]: 24 * 60,
  [NotificationType.hours]: 60,
  [NotificationType.minutes]: 1,
};
const defaultValues: Record<NotificationType, string> = {
  [NotificationType.days]: '1',
  [NotificationType.hours]: '1',
  [NotificationType.minutes]: '30',
};

export function CustomNotificationModal({
  initialValue,
  isOpen,
  onClose,
  onSubmit,
}: InviteInstructionModalProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(initialValue ?? defaultValues.minutes);
  const [notificationType, setNotificationType] = useState(
    NotificationType.minutes,
  );

  const notificationTypeOptions = [
    {
      label: t('all:event.notifications.labels.long.m'),
      value: NotificationType.minutes,
    },
    {
      label: t('all:event.notifications.labels.long.h'),
      value: NotificationType.hours,
    },
    {
      label: t('all:event.notifications.labels.long.d'),
      value: NotificationType.days,
    },
  ];
  const labels: Record<NotificationType, string> = {
    [NotificationType.days]: t('all:event.notifications.labels.short.d'),
    [NotificationType.hours]: t('all:event.notifications.labels.short.h'),
    [NotificationType.minutes]: t('all:event.notifications.labels.short.m'),
  };

  useMainButton({
    isDisabled: !value,
    isHidden: !isOpen,
    onClick: () => onSubmit(`${Number(value) * multipliers[notificationType]}`),
    text: initialValue
      ? t('all:event.edit_from.notifications.edit_custom')
      : t('all:event.edit_from.notifications.submit_custom'),
  });

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 1);
    }
  }, [isOpen]);

  useEffect(() => {
    setValue(defaultValues[notificationType]);
    inputRef.current?.focus();
  }, [notificationType]);

  useEffect(() => {
    if (isOpen) {
      if (initialValue) {
        const newValue = Number(initialValue);
        const newNotificationType = getNotificationType(Number(initialValue));
        setValue(`${newValue / multipliers[newNotificationType]}`);
        setNotificationType(newNotificationType);
      } else {
        setValue(defaultValues.minutes);
        setNotificationType(NotificationType.minutes);
      }
    }
  }, [isOpen]);

  return (
    <Sheet onOpenChange={onClose} open={isOpen}>
      <SheetContent
        className="bg-tg-secondary-bg h-full w-full overflow-y-scroll border-none p-5"
        onOpenAutoFocus={(event) => event.preventDefault()}
        side="right"
      >
        <div className="mt-20 flex flex-col items-center gap-6">
          <CustomNotificationInput
            label={labels[notificationType]}
            maxValue={maxValues[notificationType]}
            onChange={setValue}
            ref={inputRef}
            value={value}
          />
          <RadioChips
            onChange={(value) => setNotificationType(value as NotificationType)}
            options={notificationTypeOptions}
            value={notificationType}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}
